import { http } from '@/plugins/http'
import { normalize, schema } from 'normalizr'
import {
  RECEIVE_TAXCODES,
  RECEIVING_TAXCODES,
  RECEIVE_TAXCODE_METADATA,
  RECEIVE_TAXCODE_FAILURE,
  SAVING_TAXCODE,
  SAVING_TAXCODE_FAILURE,
  UPDATING_TAXCODE,
  UPDATING_TAXCODE_FAILURE,
  DELETE_TAXCODE,
  DELETING_TAXCODE,
  DELETING_TAXCODE_FAILURE,
  CLEAR_TAXCODE_STATE,
  RESET_TAXCODE_META,
  CLEAR_STATE,
  INCREMENT_TAXCODE_COUNT
} from '../mutationTypes'
import { checkStatus, getHeaders, parseJSON, buildUrlWithQuery, buildUrl } from '@/utils/api'
import handleXHRError from '@/utils/helpers/HandleXHRError'
import {
  receiveData,
  deleteData,
  increment,
  receiveMetadata,
  resetMetadata,
  clearLocalState,
  clearState
} from '@/store/modules/helpers/mutationHelpers'

const taxcodeSchema = new schema.Entity('taxcodes')
const taxcodeListSchema = new schema.Array(taxcodeSchema)

const state = {
  data: {},
  cache: {},
  count: null,
  shown: null,
  next: null,
  previous: null,
  currentPage: 1,
  saving: false,
  saveFailure: null,
  receivingData: false,
  receiveDataFailure: false,
  updating: null,
  updateFailure: null,
  deleting: false,
  deleteFailure: null
}

const actions = {
  fetchTaxcodesWithQuery (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_TAXCODES, true)
    return http.get(buildUrlWithQuery('countrytaxcodes', query, instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_TAXCODES, false)
        this.commit(RESET_TAXCODE_META)
        if (!r.hasOwnProperty('results')) return this.commit(RECEIVE_TAXCODE_FAILURE, 'Error fetching taxcodes')
        const { results, next, previous, count } = r
        const { entities, result } = normalize(results, taxcodeListSchema)
        this.commit(RECEIVE_TAXCODES, { data: entities.taxcodes })
        this.commit(RECEIVE_TAXCODE_METADATA, { next, previous, count, result: result.length })
        return { query, count, entities }
      })
      .catch(e => {
        this.commit(RECEIVING_TAXCODES, false)
        this.commit(RECEIVE_TAXCODE_FAILURE, handleXHRError(e))
      })
  },
  fetchTaxCodes (store, payload) {
    const { query, endpoint } = payload
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    return http({ url: buildUrl(endpoint, query || null, instanceUrl), headers, method: 'get' })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        if (!r) return Promise.reject()
        return r
      })
      .catch(e => {
        console.log(e)
        return handleXHRError(e)
      })
  }
}

const getters = {
  getTaxcodes: (state) => {
    return state.data ? Object.values(state.data).map(v => Object.assign({}, v)) : []
  },
  getAllTaxcodes: (state) => {
    return state.cache ? Object.values(state.cache).map(v => Object.assign({}, v)) : []
  },
  getTaxcodeMetadata: (state) => {
    const { shown, count } = state
    if (!shown) return { count: 0, shown: 0 }
    return { count, shown }
  },
  getTaxcodeById: (state) => (id) => {
    if (state.data && state.data[id]) {
      return state.data[id]
    } else if (state.cache && state.cache[id]) {
      return state.cache[id]
    } else {
      return {}
    }
  },
  getTaxcodeFetchStatus: (state) => {
    return state.receivingData
  },
  getTaxcodeSaveStatus: (state) => {
    return state.saving
  },
  getTaxcodeUpdateStatus: (state) => {
    return state.updating
  },
  getTaxcodeDeleteStatus: (state) => {
    return state.deleting
  }
}

const mutations = {
  [RECEIVE_TAXCODES] (state, results) {
    receiveData(state, results)
  },
  [RECEIVING_TAXCODES] (state, payload) {
    state.receivingData = payload
  },
  [RECEIVE_TAXCODE_FAILURE] (state, error) {
    state.receiveDataFailure = error
  },
  [SAVING_TAXCODE] (state, payload) {
    state.saving = payload
  },
  [SAVING_TAXCODE_FAILURE] (state, payload) {
    state.saveFailure = payload
  },
  [UPDATING_TAXCODE] (state, payload) {
    state.updating = payload
  },
  [UPDATING_TAXCODE_FAILURE] (state, payload) {
    const { id, err } = payload
    state.updateFailure = { id, err }
  },
  [DELETE_TAXCODE] (state, payload) {
    deleteData(state, payload)
  },
  [DELETING_TAXCODE] (state, payload) {
    state.deleting = payload
  },
  [DELETING_TAXCODE_FAILURE] (state, payload) {
    const { id, err } = payload
    state.deleteFailure = { id, err }
  },
  [INCREMENT_TAXCODE_COUNT] (state, payload) {
    increment(state, payload)
  },
  [RECEIVE_TAXCODE_METADATA] (state, payload) {
    receiveMetadata(state, payload)
  },
  [CLEAR_TAXCODE_STATE] (state, payload) {
    clearLocalState(state, payload)
  },
  [RESET_TAXCODE_META] (state) {
    resetMetadata(state)
  },
  [CLEAR_STATE] (state) {
    clearState(state)
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}
